import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import questionsData from '../data/questions.json'
import { AREAS } from '../data/constants'

const TOTAL_QUESTIONS = questionsData.length
const MIN_QUESTION = questionsData[0]?.id ?? 1
const MAX_QUESTION = questionsData[questionsData.length - 1]?.id ?? MIN_QUESTION
const DEFAULT_RANGE = AREAS[0]?.range ?? [MIN_QUESTION, Math.min(MIN_QUESTION + 19, MAX_QUESTION)]

const VARIANT_CONFIG = {
  free: {
    title: '⚡ フリーモード',
    titleClass: 'text-neon-cyan text-glow-cyan',
    rangeDescription: `練習したい問題番号を指定してください。現在のデータセットは ${MIN_QUESTION} 〜 ${MAX_QUESTION} です。`,
    confirmLabel: 'この範囲でスタート',
  },
  flashcard: {
    title: '📚 フレーズリスト',
    titleClass: 'text-green-400',
    rangeDescription: '表示したいフレーズの問題番号を指定してください。番号を絞るとリストが見やすくなります。',
    confirmLabel: 'この範囲で表示',
  },
}

const clamp = (value) => {
  if (Number.isNaN(value)) return MIN_QUESTION
  return Math.min(Math.max(value, MIN_QUESTION), MAX_QUESTION)
}

export default function FreeModeSelectionScreen({ onConfirmRange, onBack, variant = 'free' }) {
  const [start, setStart] = useState(DEFAULT_RANGE[0])
  const [end, setEnd] = useState(DEFAULT_RANGE[1])
  const [isRandom, setIsRandom] = useState(false) // 順番通り(false) or ランダム(true)
  const [error, setError] = useState(null)
  const variantConfig = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.free

  const questionCount = useMemo(() => {
    if (start > end) return 0
    return end - start + 1
  }, [start, end])

  const handleStartChange = (event) => {
    const value = clamp(parseInt(event.target.value, 10))
    setStart(value)
    // 入力中はエラーをクリア
    setError(null)
  }

  const handleEndChange = (event) => {
    const value = clamp(parseInt(event.target.value, 10))
    setEnd(value)
    // 入力中はエラーをクリア
    setError(null)
  }

  const handlePresetSelect = (range) => {
    setStart(range[0])
    setEnd(range[1])
    setError(null)
  }

  const handleConfirm = () => {
    // 確認ボタンを押したときにバリデーション
    if (start > end) {
      setError('開始番号は終了番号以下にしてください。')
      return
    }
    
    setError(null)
    onConfirmRange([start, end], isRandom)
  }

  const presets = AREAS.map((area) => ({
    id: area.id,
    label: area.name,
    range: area.range,
    emoji: area.emoji,
    color: area.color,
  }))

  return (
    <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 overflow-auto relative">
      {/* キラキラ背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-xl"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              opacity: [0, 0.5, 0],
              rotate: [0, 360]
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: "easeInOut"
            }}
          >
            {['✨', '⭐', '💫'][Math.floor(Math.random() * 3)]}
          </motion.div>
        ))}
      </div>
      <div className="max-w-5xl mx-auto px-6 py-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <motion.button
            onClick={onBack}
            className="btn-neon text-sm px-4 py-2 bg-gradient-to-r from-orange-400 to-pink-400"
            whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
            whileTap={{ scale: 0.95 }}
          >
            ← 戻る
          </motion.button>
          <h1 className="font-display text-3xl md:text-4xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
            {variantConfig.title}
          </h1>
          <div className="w-24" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Range Picker */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="card-neon p-6"
          >
            <h2 className="font-display text-2xl text-purple-700 mb-4 font-black">範囲を選択</h2>
            <p className="text-purple-600 text-sm mb-6 font-semibold">{variantConfig.rangeDescription}</p>

            <div className="space-y-4">
              <div>
                <label className="text-purple-600 text-sm font-bold">開始番号</label>
                <input
                  type="number"
                  min={MIN_QUESTION}
                  max={TOTAL_QUESTIONS}
                  value={start}
                  onChange={handleStartChange}
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/80 border-2 border-purple-300 text-purple-700 focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-300 font-bold"
                />
              </div>
              <div>
                <label className="text-purple-600 text-sm font-bold">終了番号</label>
                <input
                  type="number"
                  min={MIN_QUESTION}
                  max={TOTAL_QUESTIONS}
                  value={end}
                  onChange={handleEndChange}
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/80 border-2 border-purple-300 text-purple-700 focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-300 font-bold"
                />
              </div>

              <div className="text-purple-700 text-sm font-bold">
                <p>
                  選択範囲: <span className="text-pink-600 font-display text-xl">#{start}</span> 〜{' '}
                  <span className="text-pink-600 font-display text-xl">#{end}</span>
                </p>
                <p>
                  問題数: <span className="text-purple-600 font-display text-xl">{questionCount}</span> 問
                </p>
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              {/* 順番/ランダム選択（フリーモードのみ） */}
              {variant === 'free' && (
                <div className="mt-4 pt-4 border-t-2 border-purple-300">
                  <label className="text-purple-600 text-sm mb-3 block font-bold">出題順序</label>
                  <div className="flex gap-3">
                    <motion.button
                      onClick={() => setIsRandom(false)}
                      className={`flex-1 py-2 px-4 rounded-xl border-3 font-bold transition-all ${
                        !isRandom
                          ? 'border-pink-400 bg-gradient-to-r from-pink-300 to-purple-300 text-purple-700 shadow-lg'
                          : 'border-purple-300 bg-white/60 text-purple-600 hover:border-pink-400'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      📋 順番通り
                    </motion.button>
                    <motion.button
                      onClick={() => setIsRandom(true)}
                      className={`flex-1 py-2 px-4 rounded-xl border-3 font-bold transition-all ${
                        isRandom
                          ? 'border-pink-400 bg-gradient-to-r from-pink-300 to-purple-300 text-purple-700 shadow-lg'
                          : 'border-purple-300 bg-white/60 text-purple-600 hover:border-pink-400'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      🎲 ランダム
                    </motion.button>
                  </div>
                </div>
              )}

              <motion.button
                onClick={handleConfirm}
                className="btn-neon w-full mt-4 bg-gradient-to-r from-blue-400 to-purple-400"
                whileHover={{ scale: 1.05, rotate: [0, -2, 2, 0] }}
                whileTap={{ scale: 0.95 }}
              >
                ✨ {variantConfig.confirmLabel} ✨
              </motion.button>
            </div>
          </motion.div>

          {/* Presets */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="card-neon p-6"
          >
            <h2 className="font-display text-2xl text-purple-700 mb-4 font-black">クイックセレクト</h2>
            <p className="text-purple-600 text-sm mb-6 font-semibold">
              よく使う範囲をワンタップで選択できます。
            </p>

            <div className="space-y-3">
              {presets.map((preset) => (
                <motion.button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset.range)}
                  className="w-full card-neon border-purple-300 hover:border-pink-400 hover:shadow-lg transition-all p-4 text-left"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.span
                        className="text-3xl"
                        animate={{ rotate: [0, 15, -15, 0] }}
                        transition={{ duration: 2, repeat: Infinity, delay: preset.id * 0.2 }}
                      >
                        {preset.emoji}
                      </motion.span>
                      <div>
                        <p className="font-display text-lg font-black text-purple-700">
                          {preset.label}
                        </p>
                        <p className="text-purple-600 text-sm font-semibold">
                          #{preset.range[0]} 〜 #{preset.range[1]}
                        </p>
                      </div>
                    </div>
                    <span className="text-pink-600 text-sm font-bold">{preset.range[1] - preset.range[0] + 1} 問</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
